import { useMemo, useState } from 'react';
import { Janela, Selo } from '../../../common/interface';
import { dataDoDia, moeda } from '../../formato';
import { origemRecarga, usePixCobranca } from '../../usePixCobranca';

interface PixRecargaSheetProps {
  idPacote: number | null;
  onFechar: () => void;
  /** O pagamento entrou: a aba recarrega o saldo. A janela continua aberta com o desfecho. */
  onPago: () => void;
}

/**
 * O Pix da recarga — handoff §6.
 *
 * Tinha uma cópia própria do acompanhamento (a mesma cadência, o mesmo teto e os
 * mesmos códigos terminais de `usePixCobranca`), e já divergia dela. Desde a 0094
 * usa o hook, como a fatura.
 *
 * Dois textos diziam o que não é:
 * - "Expira em 14:59" contava o teto do ACOMPANHAMENTO, não a validade do código,
 *   que vem do banco (`vencimento`);
 * - "Códigos Pix valem 15 minutos", no desfecho de expirado.
 */
export function PixRecargaSheet({ idPacote, onFechar, onPago }: PixRecargaSheetProps) {
  const [copiado, setCopiado] = useState(false);
  const [aba, setAba] = useState<'qr' | 'copia'>('qr');
  // `useMemo` pelo id: sem ele, cada redesenho criaria outra origem, e o efeito
  // do hook geraria outra cobrança.
  const origem = useMemo(() => (idPacote === null ? null : origemRecarga(idPacote)), [idPacote]);
  const { cobranca, statusEfetivo, criando, erro, tetoAtingido, restanteMs, consultarAgora } =
    usePixCobranca(origem, onPago);

  const minutos = Math.floor(restanteMs / 60_000);
  const segundos = Math.floor((restanteMs % 60_000) / 1000);
  const pago = statusEfetivo === 'CONCLUIDA' || statusEfetivo === 'CONCLUIDA_DIVERGENTE';
  const terminal = pago || statusEfetivo === 'EXPIRADA' || statusEfetivo === 'CANCELADA' || statusEfetivo === 'ERRO';

  function copiar() {
    if (!cobranca?.pixCopiaECola) return;
    navigator.clipboard?.writeText(cobranca.pixCopiaECola);
    setCopiado(true);
    setTimeout(() => setCopiado(false), 2000);
  }

  return (
    <Janela
      aberta={idPacote !== null}
      titulo="Pagamento por Pix"
      contexto="Recarga de crédito"
      apoio={statusEfetivo === 'ATIVA' && !tetoAtingido
        ? `Acompanhando o pagamento por mais ${minutos}:${String(segundos).padStart(2, '0')}.`
        : null}
      erro={erro}
      onFechar={onFechar}
      acoes={erro || terminal ? (
        <button type="button" className={`co-btn ${pago ? 'co-btn-primario' : 'co-btn-secundario'}`} onClick={onFechar}>
          Fechar
        </button>
      ) : undefined}
    >
      {(criando || statusEfetivo === 'CRIANDO') && !erro && <p className="co-apoio" role="status">Gerando o código Pix…</p>}

      {!erro && statusEfetivo === 'ATIVA' && cobranca && (
        <div className="co-pilha">
          <div className="co-cabecalho">
            <div>
              <span className="co-rotulo">Você paga</span>
              <div className="co-pix-valor">{moeda(cobranca.valor)}</div>
            </div>
            <Selo papel="atencao">Aguardando pagamento</Selo>
          </div>
          {cobranca.vencimento && <p className="co-apoio">O código vale até {dataDoDia(cobranca.vencimento)}.</p>}

          {tetoAtingido && (
            // Deixar de acompanhar em silêncio faria o cooperado achar que o
            // pagamento falhou — quando o mais provável é que só a baixa demorou.
            <p className="co-nota co-nota-atencao" role="status">
              Ainda não recebemos a confirmação. Se você já pagou, o valor entra sozinho; toque em "Já paguei" para
              verificar agora.
            </p>
          )}

          <div className="toggle-pill" role="radiogroup" aria-label="Forma de pagar">
            <input type="radio" id="co-pix-qr" name="co-pix-forma" checked={aba === 'qr'} onChange={() => setAba('qr')} />
            <label htmlFor="co-pix-qr">QR Code</label>
            <input type="radio" id="co-pix-copia" name="co-pix-forma" checked={aba === 'copia'} onChange={() => setAba('copia')} />
            <label htmlFor="co-pix-copia">Copia e cola</label>
          </div>

          {aba === 'qr' ? (
            <div className="co-pix-qr">
              <img
                src={cobranca.qrCodePngBase64
                  ? `data:image/png;base64,${cobranca.qrCodePngBase64}`
                  : `/api/cooperado/creditos/pix/${cobranca.txid}/qrcode.png`}
                alt="QR Code do Pix"
              />
              <p className="co-apoio">Abra o app do seu banco, escolha Pix e aponte a câmera.</p>
            </div>
          ) : (
            <p className="co-pix-codigo">{cobranca.pixCopiaECola}</p>
          )}

          <button type="button" className="co-btn co-btn-primario co-btn-largo" onClick={copiar}>
            {copiado ? 'Código copiado' : 'Copiar código Pix'}
          </button>
          <button type="button" className="co-btn co-btn-secundario co-btn-largo" onClick={consultarAgora}>
            Já paguei / Atualizar
          </button>
        </div>
      )}

      {!erro && statusEfetivo === 'CONCLUIDA' && (
        <Desfecho titulo="Pagamento confirmado" texto="O crédito já está no seu saldo." />
      )}
      {!erro && statusEfetivo === 'CONCLUIDA_DIVERGENTE' && (
        // Recarga é compra: o valor diferente entra como crédito pelo que foi
        // pago (regra do servidor, `pix-webhook.queue.service`).
        <Desfecho
          titulo="Recebemos seu pagamento"
          texto="O valor não conferiu com o pacote, e entrou de crédito o que você pagou. Não pague de novo."
        />
      )}
      {!erro && statusEfetivo === 'EXPIRADA' && (
        <Desfecho titulo="O código Pix expirou" texto="Nenhum valor foi cobrado. Escolha o pacote de novo para gerar outro." />
      )}
      {!erro && statusEfetivo === 'CANCELADA' && (
        <Desfecho titulo="Cobrança cancelada" texto="Nenhum valor foi cobrado." />
      )}
      {!erro && statusEfetivo === 'ERRO' && (
        <Desfecho titulo="Não foi possível gerar a cobrança" texto="Nenhum valor foi cobrado. Tente de novo." />
      )}
    </Janela>
  );
}

function Desfecho({ titulo, texto }: { titulo: string; texto: string }) {
  return (
    <div className="co-desfecho" role="status">
      <strong className="co-titulo-cartao">{titulo}</strong>
      <p className="co-apoio">{texto}</p>
    </div>
  );
}
